import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import type { AuthUser, AccessibleEntity } from '../shared/types/index.js';
import i18n, { SUPPORTED_LANGUAGES } from '../../i18n/index.js';
import { winkelUitToken } from './tokenWinkel.js';

interface AuthContextType {
  user: AuthUser | null;
  token: string | null;
  accessibleEntities: AccessibleEntity[];
  activeEntity: AccessibleEntity | null;
  dataScope: 'full' | 'sales_only';
  login: (email: string, password: string) => Promise<void>;
  register: (data: { email: string; password: string; firstName: string; lastName: string; role?: string; entityId?: string; inviteToken: string }) => Promise<void>;
  logout: () => void;
  switchEntity: (entityId: string) => Promise<void>;
  impersonate: (userId: string) => Promise<void>;
  stopImpersonate: () => Promise<void>;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);

  // Taal uit het profiel van de medewerker (Instellingen › Taal) toepassen na inloggen/sessieherstel,
  // zodat een Franstalige plaatser de app in het Frans krijgt, ook op een Nederlandstalig toestel.
  // 'nl' is de standaardwaarde voor iedereen → enkel een bewust gekozen andere taal overschrijft de
  // browser-/toestelkeuze (geen regressie voor wie nooit een taal instelde).
  useEffect(() => {
    const taal = user?.language;
    if (taal && taal !== 'nl' && SUPPORTED_LANGUAGES.some(l => l.code === taal) && i18n.language !== taal) {
      void i18n.changeLanguage(taal);
    }
  }, [user?.language]);
  const [token, setToken] = useState<string | null>(localStorage.getItem('accessToken'));
  const [loading, setLoading] = useState(true);
  const [accessibleEntities, setAccessibleEntities] = useState<AccessibleEntity[]>([]);
  const [activeEntity, setActiveEntity] = useState<AccessibleEntity | null>(null);
  const [dataScope, setDataScope] = useState<'full' | 'sales_only'>('full');

  const logout = useCallback(() => {
    setUser(null);
    setToken(null);
    setAccessibleEntities([]);
    setActiveEntity(null);
    setDataScope('full');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
  }, []);

  const updateEntityState = useCallback((entities: AccessibleEntity[], activeEntityId: string | null, scope: string) => {
    setAccessibleEntities(entities);
    setDataScope((scope as 'full' | 'sales_only') || 'full');
    const active = entities.find(e => e.id === activeEntityId) || entities[0] || null;
    setActiveEntity(active);
  }, []);

  // Alle tabbladen delen localStorage. Wisselt een ander tabblad van winkel, dan herladen we dit
  // tabblad, zodat het nooit een andere winkel toont dan die waarin de sessie werkt; logt een ander
  // tabblad uit, dan ook hier.
  useEffect(() => {
    const opWijziging = (e: StorageEvent) => {
      if (e.key !== 'accessToken') return;
      if (!e.newValue) { logout(); return; }
      const winkel = winkelUitToken(e.newValue);
      if (activeEntity && winkel && winkel !== activeEntity.id) window.location.reload();
    };
    window.addEventListener('storage', opWijziging);
    return () => window.removeEventListener('storage', opWijziging);
  }, [activeEntity, logout]);

  useEffect(() => {
    if (token) {
      fetch('/api/auth/me', { headers: { Authorization: `Bearer ${token}` } })
        .then(res => {
          if (!res.ok) throw new Error('Unauthorized');
          return res.json();
        })
        .then(data => {
          setUser({
            id: data.id,
            email: data.email,
            firstName: data.firstName,
            lastName: data.lastName,
            role: data.role,
            entityId: data.entityId,
            language: data.language,
            dataScope: data.dataScope,
            extern: !!data.extern, // externe plaatser: app-label + redirect naar /app/plaatser
          });
          updateEntityState(data.accessibleEntities || [], data.activeEntityId || data.entityId, data.dataScope || 'full');
        })
        .catch(() => {
          const refreshToken = localStorage.getItem('refreshToken');
          if (refreshToken) {
            fetch('/api/auth/refresh', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ refreshToken }),
            })
              .then(res => res.ok ? res.json() : Promise.reject())
              .then(data => {
                localStorage.setItem('accessToken', data.accessToken);
                localStorage.setItem('refreshToken', data.refreshToken);
                setToken(data.accessToken);
              })
              .catch(() => logout());
          } else {
            logout();
          }
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [token, logout, updateEntityState]);

  const login = async (email: string, password: string) => {
    const res = await fetch('/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    if (!res.ok) {
      const data = await res.json();
      throw new Error(data.message || 'Inloggen mislukt');
    }
    const data = await res.json();
    localStorage.setItem('accessToken', data.accessToken);
    localStorage.setItem('refreshToken', data.refreshToken);
    setToken(data.accessToken);
    setUser(data.user);
    updateEntityState(data.accessibleEntities || [], data.activeEntityId || data.user.entityId, data.user.dataScope || 'full');
  };

  const register = async (regData: { email: string; password: string; firstName: string; lastName: string; role?: string; entityId?: string; inviteToken: string }) => {
    const res = await fetch('/api/auth/register', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(regData),
    });
    if (!res.ok) {
      const data = await res.json();
      throw new Error(data.message || 'Registratie mislukt');
    }
    const data = await res.json();
    localStorage.setItem('accessToken', data.accessToken);
    localStorage.setItem('refreshToken', data.refreshToken);
    setToken(data.accessToken);
    setUser(data.user);
  };

  const switchEntity = async (entityId: string) => {
    const currentToken = localStorage.getItem('accessToken');
    const res = await fetch('/api/auth/switch-entity', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(currentToken ? { Authorization: `Bearer ${currentToken}` } : {}),
      },
      body: JSON.stringify({ entityId }),
    });
    if (!res.ok) {
      const data = await res.json();
      throw new Error(data.message || 'Wisselen mislukt');
    }
    const data = await res.json();
    localStorage.setItem('accessToken', data.accessToken);
    localStorage.setItem('refreshToken', data.refreshToken);
    // Hard navigate to force clean React state (reload() can use bfcache)
    window.location.href = window.location.pathname + '?t=' + Date.now();
  };

  // Impersonate target user — werkt zowel vanuit superadmin-state als vanuit
  // een al-impersonated state (snel switchen tussen test-users zonder uit
  // te loggen).
  const impersonate = async (userId: string) => {
    const currentToken = localStorage.getItem('accessToken');
    const res = await fetch('/api/auth/impersonate', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(currentToken ? { Authorization: `Bearer ${currentToken}` } : {}),
      },
      body: JSON.stringify({ userId }),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.message || 'Impersonate mislukt');
    }
    const data = await res.json();
    localStorage.setItem('accessToken', data.accessToken);
    localStorage.setItem('refreshToken', data.refreshToken);
    // Hard navigate naar dashboard zodat alle React Query caches resetten
    window.location.href = '/dashboard';
  };

  // Stop impersonation — server retourneert nieuwe tokens voor originele
  // superadmin, geen logout/login flow nodig.
  const stopImpersonate = async () => {
    const currentToken = localStorage.getItem('accessToken');
    const res = await fetch('/api/auth/stop-impersonate', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(currentToken ? { Authorization: `Bearer ${currentToken}` } : {}),
      },
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.message || 'Stoppen mislukt');
    }
    const data = await res.json();
    if (data.accessToken && data.refreshToken) {
      localStorage.setItem('accessToken', data.accessToken);
      localStorage.setItem('refreshToken', data.refreshToken);
    }
    // Hard navigate zodat alle React Query caches resetten
    window.location.href = '/dashboard';
  };

  return (
    <AuthContext.Provider value={{ user, token, accessibleEntities, activeEntity, dataScope, login, register, logout, switchEntity, impersonate, stopImpersonate, loading }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
